import Api from '@/services/Api';
import type { ActionContext } from 'vuex';
import { mutations as MUTATION, actions as ACTION } from './types';
import { base } from '@/services/Endpoint';
import type { State } from './state';
import { ProductAssignmentService } from '@/services/ProductAssignmentService';
import HELPER from '@/app/utils/HELPER.js';

/**
 * commitDefaultPageSettingForm
 * @param commit
 * @param rootGetters
 * @param newValue
 */
const commitDefaultPageSettingForm = (commit: Function, rootGetters: any, newValue: any) => {
  const pageType = rootGetters['app/pageType'];
  const pageGID = rootGetters['app/pageGID'];

  commit('SET_DEFAULT_PAGE_SETTING_FORM', {
    newValue,
    pageType,
    pageGID,
  });
};

const saveListAssigned = (products: any, list: any, cachedSavedList: any) => {
  if (products && products.length) {
    const cachedList: { id: any }[] = [];
    for (const item of products) {
      if (!list.find((listItem: { id: any }) => listItem.id === item.id)) {
        cachedList.push(item);
        cachedSavedList.push(item);
      }
      HELPER.updateProductInList(item);
    }
    list.push(...cachedList);
  }
};

const actions = {
  [ACTION.FETCH_PAGES]({ state, commit }: ActionContext<State, any>, { type }: { type: string }) {
    return new Promise(async (resolve, reject) => {
      try {
        if (!(state as any)[type].fetched) {
          const res = await Api.base.get((base.template as any)[type]);

          if (res?.data?.pages) {
            commit(MUTATION.COMMIT_PAGES, {
              pages: res.data.pages,
              type: type,
            });
          }
          (state as any)[type].fetched = true;
          resolve(res?.data?.pages || []);
        } else {
          resolve((state as any)[type].list);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        reject(error);
      }
    });
  },

  [ACTION.GET_ASSIGNED_PRODUCT]({ state, rootState }: ActionContext<State, any>, params: any, isChangeQuery: boolean) {
    return new Promise(async (resolve, reject) => {
      try {
        const { list, cachedList, listQuery, cachedListQuery, endCursor, endCursorQuery } = state.assignedProduct;
        const isQuery = params.s.length !== 0;

        const res = await ProductAssignmentService.getAssignedProduct({
          ...params,
          nextCursor: isQuery ? endCursorQuery : endCursor,
        });
        const products = res.assigned_products;
        const pageInfo = res.pageInfo;

        const isFetchedAll = products?.length == 0 || products?.length < 20 || !pageInfo?.hasNextPage;

        if (isQuery) {
          if (isFetchedAll) state.assignedProduct.fetchedAllQuery = true;
          state.assignedProduct.endCursorQuery = pageInfo?.endCursor;
          isChangeQuery
            ? (state.assignedProduct.listQuery = products)
            : saveListAssigned(products, listQuery, cachedListQuery);
        } else {
          if (isFetchedAll) state.assignedProduct.fetchedAll = true;
          state.assignedProduct.endCursor = pageInfo?.endCursor;
          saveListAssigned(products, list, cachedList);
          state.assignedProduct.total = res.total;
        }

        let idProductDynamic = state.assignedProduct.idProductDynamic;

        if (!idProductDynamic) {
          const pageSettings = rootState.app.pageInfo.page.settings;
          idProductDynamic =
            (pageSettings && pageSettings['productDynamic'] && pageSettings['productDynamic']['id']) || 'auto';
        }

        if (!isQuery) {
          if (list?.length > 0) {
            const existItem = list.find((product: any) => product.id == idProductDynamic);
            if (!existItem) {
              idProductDynamic = list[0].id;
              state.assignedProduct.statusProductDynamic = list[0].status;
            }
          } else {
            idProductDynamic = 'auto';
          }
        }

        state.assignedProduct.idProductDynamic = idProductDynamic;

        if (res.status == 'success') {
          resolve(res);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        reject(error);
      }
    });
  },

  //Page setting
  async getPageSetting({ state, commit, rootGetters }: ActionContext<State, any>) {
    if (state.getPageSettingSuccess) {
      const currentFormString = JSON.stringify(state.pageSettingForm);
      const defaultFormString = JSON.stringify(state.defaultPageSettingForm);

      if (currentFormString == defaultFormString) {
        state.resetPageSettingChange();
      }
      return;
    }

    const pageID = rootGetters['app/pageId'];
    if (!pageID) throw 'Page id is not found';

    try {
      state.loadingPageSetting = true;
      const response: any = await Api.base.get(base.pageSetting(pageID));
      const responseData: any = response.data;

      if (responseData.status == 'success') {
        commitDefaultPageSettingForm(commit, rootGetters, responseData.setting_data);
        state.getPageSettingSuccess = true;
      }
    } finally {
      state.loadingPageSetting = false;
    }
  },
  async savePageSetting({ state, commit, rootGetters }: ActionContext<State, any>) {
    const pageID = rootGetters['app/pageId'];
    if (!pageID) throw 'Page id is not found';

    const pageSetting = state.pageSettingForm;

    if ((pageSetting.general as any)['featured_image']) {
      (pageSetting.general as any)['featured_image'] = undefined;
    }

    const payload = {
      pageSetting,
    };

    const response: any = await Api.base.put(base.pageSetting(pageID), payload);
    const responseData: any = response.data;

    if (responseData.status == 'success') {
      state.resetPageSettingChange();
      commitDefaultPageSettingForm(commit, rootGetters, responseData.setting_data);
      return state.pageSettingForm;
    }
  },
};

export default actions;
